/**
 * Localized data helpers — overlay French data files with English translations.
 *
 * Usage:
 *   import { localize } from './data/i18nData';
 *   const event = localize('event', baseEvent);
 *   // event.name / event.choices[*].label are now in the active language
 */
import eventsEN from './translations/events_en.json';
import advisorsEN from './translations/advisors_en.json';
import banksEN from './translations/banks_en.json';
import competitorsEN from './translations/competitors_en.json';
import contractsEN from './translations/contracts_en.json';
import driversEN from './translations/drivers_en.json';
import vehiclesEN from './translations/vehicles_en.json';
import difficultyEN from './translations/difficulty_en.json';
import investmentsEN from './translations/investments_en.json';
import { useLanguageStore } from '../i18n';

const SOURCES_EN = {
  event: eventsEN,
  contract: contractsEN,
  advisor: advisorsEN.ADVISORS,
  advisorMood: advisorsEN.ADVISOR_MOODS,
  eventReaction: advisorsEN.EVENT_REACTIONS,
  situationalReaction: advisorsEN.SITUATIONAL_REACTIONS,
  bank: banksEN.BANKS,
  creditScore: banksEN.CREDIT_SCORES,
  scoreReveal: banksEN.SCORE_REVEAL_MESSAGES,
  placement: banksEN.PLACEMENT_PRODUCTS,
  loyaltyLabel: banksEN.LOYALTY_LABELS,
  competitor: competitorsEN.COMPETITORS,
  competitionEvent: competitorsEN.COMPETITION_EVENTS,
  abroadCost: competitorsEN.ABROAD_COSTS,
  driver: driversEN,
  vehicle: vehiclesEN,
  difficulty: difficultyEN,
  investment: investmentsEN,
};

/** Current language (read directly, not via hook so it works outside React). */
const getLang = () => useLanguageStore.getState().language;

/**
 * Deep-merge an EN overlay onto a FR object.
 * Returns a new object — never mutates input.
 * If lang === 'fr' OR overlay missing, returns the original object.
 */
function applyOverlay(obj, overlay) {
  if (!overlay) return obj;
  if (Array.isArray(obj) && Array.isArray(overlay)) {
    return obj.map((item, i) => applyOverlay(item, overlay[i]));
  }
  if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
    const out = { ...obj };
    for (const k of Object.keys(overlay)) {
      if (overlay[k] !== undefined && overlay[k] !== null) {
        out[k] = applyOverlay(obj[k], overlay[k]);
      }
    }
    return out;
  }
  // Primitive — use overlay if present
  return overlay !== undefined && overlay !== null ? overlay : obj;
}

/**
 * Localize a single entity for the current language.
 * @param {keyof typeof SOURCES_EN} kind  e.g. 'event', 'advisor', 'bank'
 * @param {object} obj                    the source FR object with an `id` field
 * @returns object — same shape, fields swapped to EN when language is 'en'
 */
export function localize(kind, obj) {
  if (!obj) return obj;
  if (getLang() !== 'en') return obj;
  const src = SOURCES_EN[kind];
  if (!src) return obj;
  const overlay = obj.id ? src[obj.id] : src;
  return applyOverlay(obj, overlay);
}

/**
 * Localize a list of entities.
 */
export function localizeList(kind, list) {
  if (!Array.isArray(list)) return list;
  return list.map((o) => localize(kind, o));
}

/**
 * Localize an indexed lookup by key (not by .id).
 * Useful for EVENT_REACTIONS[category][advisorId].dialogues etc.
 */
export function localizeByKey(kind, key, obj) {
  if (!obj) return obj;
  if (getLang() !== 'en') return obj;
  const src = SOURCES_EN[kind];
  if (!src) return obj;
  return applyOverlay(obj, src[key]);
}

/** Translate just one string field via overlay. */
export function locField(kind, id, field, fallback) {
  if (getLang() !== 'en') return fallback;
  const entry = SOURCES_EN[kind] && SOURCES_EN[kind][id];
  if (!entry) return fallback;
  return entry[field] !== undefined ? entry[field] : fallback;
}
